import React, { useState } from 'react';
import { useAuthQuery as useQuery } from '../../../../shared/hooks/useAuthQuery';
import { Button } from '../../../../catalyst-components/button';
import { useNavigate } from 'react-router-dom';
import { AdminRoutes } from '../../../../shared/models';
import { useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { ArrowLeftIcon, PlusIcon, ArrowUpIcon, ArrowDownIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../../../catalyst-components/accordion';
import { ConfirmationDialog } from '../../../../catalyst-components/confirmation-dialog';
import { Id } from '../../../../convex/_generated/dataModel';
import { SkillAssistantTable } from './components/skill-assistant-table';

export const Skills: React.FC = () => {
  const navigate = useNavigate();
  const skills = useQuery(api.skills.list) || [];
  const skill_assistants = (useQuery(api.skill_assistants.list) || []) as any[];
  const deleteSkill = useMutation(api.skills.remove);
  const moveSkillUp = useMutation(api.skills.moveUp);
  const moveSkillDown = useMutation(api.skills.moveDown);
  const [confirmDeleteSkill, setConfirmDeleteSkill] = useState<{
    open: boolean;
    skill?: { _id: Id<"skills">; name: string };
  }>({ open: false });

  // Sort skills by sequence number to ensure proper order
  const sortedSkills = [...skills].sort((a, b) => a.sequence - b.sequence);

  const handleMoveUp = async (skillId: Id<"skills">) => {
    try {
      await moveSkillUp({ id: skillId });
    } catch (error) {
    }
  };

  const handleMoveDown = async (skillId: Id<"skills">) => {
    try {
      await moveSkillDown({ id: skillId });
    } catch (error) {
    }
  };

  return (
    <div className="min-h-screen bg-background pt-24">
      {/* Header */}
      <div className="border-b border-border/50 bg-gradient-to-r from-primary/5 to-accent/5 px-6 py-8">
        <div>
          <div className="flex items-center gap-4 mb-4">
            <Button
              color="secondary"
              className="rounded-full"
              onClick={() => navigate('../')}
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-semibold text-foreground mb-1">Manage Skills</h1>
              <p className="text-base text-muted-foreground">
                Configure skill trees and progression paths for users
              </p>
            </div>
            <Button
              color="primary"
              className="rounded-full"
              onClick={() => navigate(`${AdminRoutes.Skills}/new`)}
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add New Skill
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-8">
        <div>
          <Accordion type="single" collapsible className="w-full space-y-4">
            {sortedSkills.map((skill, index) => (
              <AccordionItem
                key={skill._id}
                value={skill._id}
                className="border-2 border-border/50 rounded-xl bg-card overflow-hidden"
              >
                <AccordionTrigger
                  className="text-xl font-semibold px-6 py-4 hover:bg-muted/50"
                  aria-controls={skill._id}
                >
                  <span className="flex items-center gap-3">
                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary text-sm font-bold">
                      {skill.sequence}
                    </span>
                    {skill.name}
                  </span>
                </AccordionTrigger>
                <AccordionContent value={skill._id}>
                  <div className="px-6 pb-6 space-y-4">
                    <div className="flex justify-between items-start gap-4 p-4 bg-muted/30 rounded-xl">
                      <p className="text-muted-foreground flex-1">{skill.description}</p>
                      <div className="flex gap-2 flex-shrink-0">
                        <Button
                          color="primary"
                          className="rounded-full"
                          onClick={() => navigate(`${AdminRoutes.Skills}/${skill._id}`)}
                        >
                          <PencilIcon className="h-4 w-4 mr-2" />
                          Manage
                        </Button>
                        <Button
                          color="secondary"
                          className="rounded-full"
                          disabled={index === 0}
                          onClick={() => handleMoveUp(skill._id)}
                          title={index === 0 ? "Already at the top" : "Move up"}
                        >
                          <ArrowUpIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          color="secondary"
                          className="rounded-full"
                          disabled={index === sortedSkills.length - 1}
                          onClick={() => handleMoveDown(skill._id)}
                          title={index === sortedSkills.length - 1 ? "Already at the bottom" : "Move down"}
                        >
                          <ArrowDownIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          color="red"
                          className="rounded-full"
                          onClick={() => setConfirmDeleteSkill({
                            open: true,
                            skill: {
                              _id: skill._id,
                              name: skill.name
                            }
                          })}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <SkillAssistantTable
                      skill_id={skill._id}
                      assistants={skill_assistants.filter(assistant => assistant.skill_id === skill._id)}
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>

      <ConfirmationDialog
        open={confirmDeleteSkill.open}
        onClose={() => setConfirmDeleteSkill({ open: false })}
        onConfirm={async () => {
          if (!confirmDeleteSkill.skill) return;
          try {
            await deleteSkill({
              id: confirmDeleteSkill.skill._id,
            });
          } catch (error) {
          }
        }}
        title="Delete Skill"
        description={`Are you sure you want to delete "${confirmDeleteSkill.skill?.name}"? This will permanently remove the skill and all its assistants. This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isCritical={true}
      />
    </div>
  );
};

// Export as Phases for backward compatibility with routes
export const Phases = Skills;